/**
 * Flower stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const flowerStickersPack: ToilePack = {
    slug: 'flower-stickers',
    category: 'sticker',
    name: 'Flower stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'daisy', label: 'Daisy', subject: 'a single white daisy with a yellow centre'},
        {key: 'sunflower', label: 'Sunflower', subject: 'a sunflower with a big brown centre and pointed petals'},
        {key: 'smiley', label: 'Smiley flower', subject: 'a retro flower with a smiling face at its centre'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a rose instead', 'for a journal cover', 'in spring colors'],
    directionHint: 'try: a rose instead',
    seo: {
        title: 'Flower Stickers: Daisy, Sunflower, Smiley',
        metaDescription: 'Nine flower stickers — a daisy, a sunflower, a retro smiley flower — in retro, bold cartoon, and minimal line styles.',
        h1: 'Flower stickers: daisy, sunflower, smiley flower',
        tagline: 'Nine stickers, three flowers, three styles each.',
        intro: 'A white daisy with a yellow centre, a sunflower with pointed petals, and a retro flower with a smiling face — three flowers as die-cut stickers. Each rendered retro, bold cartoon, and minimal single-line. Ask for a different flower, a bouquet composition, or a garden scene, and the same three styles carry it.',
        useCases: [
            'a water bottle or laptop lid',
            'a planner or journal spread',
            'a greeting card or gift wrap',
            'a floral-themed sticker sheet',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine flower stickers — a daisy, a sunflower, and a retro smiley flower — in retro, bold cartoon, and minimal line finishes.'},
            {q: 'Can I get a different flower, like a rose or tulip?', a: 'Yes — the direction field takes any variation, including a different flower, a bouquet composition, or a garden scene.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
            {q: 'Are these botanically accurate?', a: 'No — these are decorative illustrations styled for stickers, not a botanical reference.'},
        ],
    },
}
